import React, {useCallback, useEffect, useRef} from 'react';

import {SsoAuditRecord} from 'src/openapi/admin/models/SsoAuditRecord';
import {formatTimestamp} from 'src/utils/date-utils';

import Button from '@pb/kit-component-button';
import {
    Drawer,
    DrawerFooter,
    DrawerHeader,
    DrawerHole,
    DrawerMain,
} from '@pb/kit-component-drawer';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {formatInitiator} from '../../utils/format-initiator';

import {auditLogDrawer} from './audit-log-drawer.module.styl';

type AuditLogDrawerProps = {
    record: SsoAuditRecord | null,
    onClose: () => void,
    getTitleById: (id: string) => string | undefined,
};

export const AuditLogDrawer = React.memo(({record, onClose, getTitleById}: AuditLogDrawerProps) => {
    const tabsRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        tabsRef.current = document.querySelector('.sticky-element');
    }, []);

    const handleSetOpen = useCallback((shown: boolean) => {
        if (!shown) {
            onClose();
        }
    }, [onClose]);

    const open = record != null;
    const changes = record?.changes;

    return (
        <Drawer
            open={open}
            setOpen={handleSetOpen}
            placement="right"
            stickToElement={tabsRef.current || undefined}
            className={auditLogDrawer}
            style={{width: 'fit-content'}}>
            <DrawerHeader>
                <span className="enable-selection">
                    {record != null ? (getTitleById(record.actionName) ?? record.actionName) : ''}
                </span>
            </DrawerHeader>
            <DrawerMain>
                <DrawerHole>
                    <div style={{width: 700}} className="enable-selection">
                        {record != null ? (
                            <Group direction="v" alignItems="start">
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Action Type</Label>
                                    <div>{getTitleById(record.actionName) ?? record.actionName}</div>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Action Description</Label>
                                    <p>{record.actionDescription || '--'}</p>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Initiator System</Label>
                                    <div>{record.initiatorSystem || '--'}</div>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Initiator System Process</Label>
                                    <div>{record.initiatorSystemProcess || '--'}</div>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Initiator</Label>
                                    <div>{formatInitiator(record)}</div>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Timestamp</Label>
                                    <div>{formatTimestamp(record.timestamp)}</div>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Reason</Label>
                                    <div>{record.reason || '--'}</div>
                                </Group>
                                <Group direction="v" alignItems="start" gap="xs">
                                    <Label>Details</Label>
                                    {(changes != null && changes.length > 0) ? (
                                        <Table borderedGrid={true}>
                                            <Head>
                                                <Cell>Field</Cell>
                                                <Cell>Old Value</Cell>
                                                <Cell>New Value</Cell>
                                            </Head>
                                            <Body>
                                                {changes.map((change, index) => (
                                                    <Row key={index}>
                                                        <Cell>
                                                            <EllipsisTextHint title={change.field}>
                                                                {change.field}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint title={change.oldValue}>
                                                                {change.oldValue || '--'}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint title={change.newValue}>
                                                                {change.newValue || '--'}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                    </Row>
                                                ))}
                                            </Body>
                                        </Table>
                                    ) : (
                                        <div>
                                            No attribute changes were detected. This log entry may have been
                                            triggered by an action that did not modify any data.
                                        </div>
                                    )}
                                </Group>
                            </Group>
                        ) : null}
                    </div>
                </DrawerHole>
            </DrawerMain>
            <DrawerFooter>
                <Button variant="Type.PRIMARY" onClick={onClose}>
                    Close
                </Button>
            </DrawerFooter>
        </Drawer>
    );
});
